import { Experiment as ExperimentClientSDK, ExperimentClient, ExperimentUser, Exposure } from '@amplitude/experiment-js-client';
import { AMPLITUDE_ANALYTICS_KEY, DEPLOY_ENV } from '@reverbdotcom/env';
import { IUser } from '../components/user_context_provider';
import windowWrapper from '../window_wrapper';
import { EventType } from '../elog/mparticle_types';
import { mParticleReady } from '../elog/mparticle';
import * as elog from '../elog';

// We might want an automated way of syncing known experiment names from Amplitude,
// or even providing fallbacks / initial variants, but hardcoding a few names
// for AA tests seems ok.
export const AMPLITUDE_EXPERIMENT_NAMES = {
  WEB_AA_HOME: 'web_aa_home',
  WEB_AA_ITEM: 'web_aa_item',
  WEB_AA_MP: 'web_aa_mp',
};

const trackExposureWithMParticle = (exposure: Exposure) => {
  const formattedExposure = {
    ...exposure,
    // Fixes a warning:
    // mparticle.js:32 For '$exposure', the corresponding attribute value of 'metadata' must be a string, number, boolean, or null.
    metadata: JSON.stringify(exposure.metadata),
  };

  mParticleReady(
    () => {
      windowWrapper.mParticle.logEvent(
        '$exposure',
        EventType.Other,
        formattedExposure,
      );
    },
  );
};

const experimentClient: ExperimentClient = ExperimentClientSDK.initialize(
  AMPLITUDE_ANALYTICS_KEY,
  {
    debug: DEPLOY_ENV !== 'production',
    fetchOnStart: true,
    /**
     * fetchTimeoutMillis is used with a non-blocking request to fetch
     * variants from Amplitude when the experiment SDK is initialized,
     * so the long value is ok. If the timeout is exceeded it does not
     * cause rendering errors.
     */
    fetchTimeoutMillis: 7000,
    pollOnStart: false,
    automaticExposureTracking: false,
    exposureTrackingProvider: {
      track: trackExposureWithMParticle,
    },
  },
);

let isAmplitudeReady = false;
type TrackedEventFn = () => void;
const trackedEventsQueue: Array<TrackedEventFn> = [];

export function initializeAmplitudeAnalytics(user: IUser): void {
  if (isAmplitudeReady) {
    return;
  }

  mParticleReady(
    () => {
      const experimentUser: ExperimentUser = {
        // Reverb database ID for logged in users, null for logged out users.
        // This is the same value that the mParticle `customerid` field is set to:
        // https://github.com/reverbdotcom/frontend/blob/main/commons/src/elog/mparticle.ts#L100
        user_id: user.id,

        // A uuid that is automatically generated and set by the mParticle SDK,
        // present for logged in and logged out users.
        device_id: windowWrapper.mParticle.getDeviceId(),
      };

      try {
        experimentClient.start(experimentUser).then(setAmplitudeReady);
      } catch (ex) {
        elog.error('amplitude_experiments.error.start', {}, ex);
      }
    },
  );
}

function setAmplitudeReady() {
  isAmplitudeReady = true;

  while (trackedEventsQueue.length) {
    const trackEvent = trackedEventsQueue.shift();
    trackEvent();
  }
}

/**
 * Tracks an [exposure event](https://www.docs.developers.amplitude.com/experiment/general/experiment-event-tracking/#exposure-event-definition)
 * (qualifying event) for the current user and an Amplitude experiment.
 */
export const trackExposure = (experimentName: string) => {
  onAmplitudeReady(
    () => {
      experimentClient.exposure(experimentName);
    },
  );
};

const onAmplitudeReady = (trackEvent: TrackedEventFn) => {
  if (isAmplitudeReady) {
    trackEvent();
    return;
  }

  trackedEventsQueue.push(trackEvent);
};
